import { effect, inject, Injectable, Injector, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TitleStrategy } from '@angular/router';
import type { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ADMIN_AREA_LABEL, ADMIN_REGISTRY_TOKEN, AdminGroupKey, findAdminGroup } from '@/core/layout/admin-registry';
import { APP_REGISTRY_TOKEN, AppKey, findApp } from '@/core/layout/app-registry';
import { MastheadContext } from '@/core/layout/masthead-context.service';

export const DOCUMENT_TITLE_BASE = 'Envolve Hub';
const SEPARATOR = ' · ';

/**
 * What the activated route says: the area it belongs to, outermost last
 * (`['Gemba']` for an app, `['Teams', 'Administration']` for an admin group,
 * `[]` elsewhere), and the deepest crumb.
 */
interface RouteTitleParts {
  readonly area: readonly string[];
  readonly breadcrumb: string | null;
}

/**
 * Document title from the same route data as the breadcrumb and the masthead
 * (SCRUM-595, masthead epic story 8; Administration in SCRUM-621):
 *
 * - app route (`data.app`):            "<last crumb> · <App name> · Envolve Hub"
 * - admin route (`data.adminGroup`):   "<last crumb> · <Group> · Administration · Envolve Hub"
 * - any other labelled route:          "<last crumb> · Envolve Hub"
 * - nothing labelled:                  "Envolve Hub"
 *
 * The last crumb is the page-declared {@link MastheadContext.crumb} when set,
 * else the deepest `data.breadcrumb`; a crumb that merely repeats the app or
 * group name is dropped ("Gemba · Envolve Hub", "Teams · Administration ·
 * Envolve Hub"). The title follows the crumb signal, so it updates when an
 * entity title arrives and resets on the next navigation. Provided in
 * `app.config.ts` as the router's `TitleStrategy`.
 */
@Injectable()
export class AppTitleStrategy extends TitleStrategy {
  private readonly injector = inject(Injector);
  private readonly title = inject(Title);
  private readonly registry = inject(APP_REGISTRY_TOKEN);
  private readonly adminRegistry = inject(ADMIN_REGISTRY_TOKEN);

  private readonly route = signal<RouteTitleParts>({ area: [], breadcrumb: null });
  private watching = false;

  override updateTitle(snapshot: RouterStateSnapshot): void {
    let area: readonly string[] = [];
    let breadcrumb: string | null = null;
    for (let route: ActivatedRouteSnapshot | null = snapshot.root; route; route = route.firstChild) {
      const appKey = route.data['app'] as AppKey | undefined;
      const groupKey = route.data['adminGroup'] as AdminGroupKey | undefined;
      if (appKey) {
        area = [findApp(appKey, this.registry).name];
      } else if (groupKey) {
        area = [findAdminGroup(groupKey, this.adminRegistry).label, ADMIN_AREA_LABEL];
      }
      breadcrumb = (route.data['breadcrumb'] as string | undefined) ?? breadcrumb;
    }
    this.route.set({ area, breadcrumb });
    this.watch();
  }

  /**
   * Starts the title effect on the first navigation. `MastheadContext` listens
   * to the `Router`, and the router builds its `TitleStrategy` while it is
   * itself being constructed, so injecting the context in the constructor
   * would be a DI cycle (NG0200); by the first `updateTitle` the router exists.
   */
  private watch(): void {
    if (this.watching) {
      return;
    }
    this.watching = true;
    const context = this.injector.get(MastheadContext);
    effect(
      () => {
        const { area, breadcrumb } = this.route();
        this.title.setTitle(buildDocumentTitle(context.crumb() ?? breadcrumb, area));
      },
      { injector: this.injector },
    );
  }
}

/**
 * "<crumb> · <area…> · Envolve Hub", dropping empty parts and a crumb that
 * merely repeats the innermost area name. `area` is the app name alone, or
 * the admin group followed by "Administration", or empty outside both.
 */
export function buildDocumentTitle(crumb: string | null, area: readonly string[]): string {
  const parts = [crumb !== area[0] ? crumb : null, ...area, DOCUMENT_TITLE_BASE];
  return parts.filter((part): part is string => !!part).join(SEPARATOR);
}
